import {
  Component,
  input,
  output,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  signal,
  computed,
  OnInit,
  AfterViewInit,
  OnDestroy,
  inject,
  PLATFORM_ID,
  ChangeDetectorRef,
} from "@angular/core";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { AgentChatMessageView } from "./ai-chat-message-view";
import { AgentChatViewScrollToBottomButton } from "./ai-chat-view-scroll-to-bottom-button";
import { StickToBottom } from "../directives/stick-to-bottom";
import { ScrollPosition } from "../utils/scroll-position";
import { Message } from "@ag-ui/client";
import { cn } from "../utils/utils";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  standalone: true,
  selector: "agent-chat-view-scroll-view",
  imports: [CommonModule, ScrollingModule, AgentChatMessageView, AgentChatViewScrollToBottomButton, StickToBottom],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [ScrollPosition],
  template: `
    @if (!hasMounted()) {
    <div class="achat-scroll-loading">
      <div class="achat-scroll-content">
        <ng-content></ng-content>
      </div>
    </div>
    } @else if (!autoScroll()) {
    <div class="achat-scroll-wrapper">
      <div #scrollContainer cdkScrollable [class]="computedClass()" class="achat-scroll-viewport">
        <div #contentContainer class="achat-scroll-content">
          <div [style.padding-bottom.px]="paddingBottom()">
            <div class="achat-scroll-messages">
              <agent-chat-message-view
                [messages]="messages()"
                [inputClass]="messageViewClass()"
                [showCursor]="showCursor()"
                (assistantMessageThumbsUp)="assistantMessageThumbsUp.emit($event)"
                (assistantMessageThumbsDown)="assistantMessageThumbsDown.emit($event)"
                (assistantMessageReadAloud)="assistantMessageReadAloud.emit($event)"
                (assistantMessageRegenerate)="assistantMessageRegenerate.emit($event)"
                (userMessageCopy)="userMessageCopy.emit($event)"
                (userMessageEdit)="userMessageEdit.emit($event)"
              >
              </agent-chat-message-view>
            </div>
          </div>
        </div>
      </div>

      @if (showScrollButton() && !isResizing()) {
      <div class="achat-scroll-to-bottom-wrap" [style.bottom.px]="inputContainerHeight() + 16">
        <agent-chat-view-scroll-to-bottom-button (clicked)="scrollToBottom()">
        </agent-chat-view-scroll-to-bottom-button>
      </div>
      }
    </div>
    } @else {
    <div class="achat-scroll-wrapper">
      <div
        #scrollContainer
        cdkScrollable
        copilotStickToBottom
        [enabled]="autoScroll()"
        [threshold]="10"
        [debounceMs]="0"
        [initialBehavior]="'smooth'"
        [resizeBehavior]="'smooth'"
        (isAtBottomChange)="onIsAtBottomChange($event)"
        [class]="computedClass()"
        class="achat-scroll-viewport"
      >
        <div class="achat-scroll-content">
          <div [style.padding-bottom.px]="paddingBottom()">
            <div class="achat-scroll-messages">
              <agent-chat-message-view
                [messages]="messages()"
                [inputClass]="messageViewClass()"
                [showCursor]="showCursor()"
                (assistantMessageThumbsUp)="assistantMessageThumbsUp.emit($event)"
                (assistantMessageThumbsDown)="assistantMessageThumbsDown.emit($event)"
                (assistantMessageReadAloud)="assistantMessageReadAloud.emit($event)"
                (assistantMessageRegenerate)="assistantMessageRegenerate.emit($event)"
                (userMessageCopy)="userMessageCopy.emit($event)"
                (userMessageEdit)="userMessageEdit.emit($event)"
              >
              </agent-chat-message-view>
            </div>
          </div>
        </div>
      </div>

      @if (!isAtBottom() && !isResizing()) {
      <div class="achat-scroll-to-bottom-wrap" [style.bottom.px]="inputContainerHeight() + 16">
        <agent-chat-view-scroll-to-bottom-button (clicked)="scrollToBottomFromStick()">
        </agent-chat-view-scroll-to-bottom-button>
      </div>
      }
    </div>
    }
  `,
})
export class AgentChatViewScrollView implements OnInit, AfterViewInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);

  autoScroll = input<boolean>(true);

  inputContainerHeight = input<number>(0);

  isResizing = input<boolean>(false);

  inputClass = input<string | undefined>();

  messages = input<Message[]>([]);

  messageViewClass = input<string | undefined>();

  showCursor = input<boolean>(false);

  assistantMessageThumbsUp = output<{ message: Message }>();

  assistantMessageThumbsDown = output<{ message: Message }>();

  assistantMessageReadAloud = output<{ message: Message }>();

  assistantMessageRegenerate = output<{ message: Message }>();

  userMessageCopy = output<{ message: Message }>();

  userMessageEdit = output<{ message: Message }>();

  @ViewChild("scrollContainer", { read: ElementRef })
  scrollContainer?: ElementRef<HTMLElement>;

  @ViewChild("contentContainer", { read: ElementRef })
  contentContainer?: ElementRef<HTMLElement>;

  @ViewChild(StickToBottom) stickToBottomDirective?: StickToBottom;

  protected hasMounted = signal(false);

  protected showScrollButton = signal(false);

  protected isAtBottom = signal(true);

  protected paddingBottom = computed(() => Math.max(this.inputContainerHeight() + 32, 96));

  protected computedClass = computed(() => cn(this.inputClass()));

  private destroy$ = new Subject<void>();

  private platformId = inject(PLATFORM_ID);

  private scrollPositionService = inject(ScrollPosition);

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.hasMounted.set(true);
      }, 0);
    }
  }

  ngAfterViewInit(): void {
    if (!this.autoScroll()) {
      setTimeout(() => {
        if (this.scrollContainer) {
          const initialState = this.scrollPositionService.getScrollState(this.scrollContainer.nativeElement, 10);
          this.showScrollButton.set(!initialState.isAtBottom);
          this.scrollPositionService
            .monitorScrollPosition(this.scrollContainer, 10)
            .pipe(takeUntil(this.destroy$))
            .subscribe((state) => {
              this.showScrollButton.set(!state.isAtBottom);
            });
        }
      }, 100);
    }
  }

  onIsAtBottomChange(isAtBottom: boolean): void {
    this.isAtBottom.set(isAtBottom);
  }

  scrollToBottom(): void {
    if (this.scrollContainer) {
      this.scrollPositionService.scrollToBottom(this.scrollContainer, true);
    }
  }

  scrollToBottomFromStick(): void {
    if (this.stickToBottomDirective) {
      this.stickToBottomDirective.scrollToBottom("smooth");
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
